import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import ModalPortal from "../../../components/Modal/ModalPortal"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import InputField from "../../../components/InputField/InputField"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { EAccountPlansType } from "../../../Enum/EAccountPlansType"
import { EBillingPeriodType } from "../../../Enum/EBillingPeriodType"
import { EChargeMethodType } from "../../../Enum/EChargeMethodType"
import Pricing from "../../../components/LandPage/Pricing"
import { companyMemory } from "../../../Objects/Memory/Memory"
import { PixPaymentPanel } from "../../../components/Payment/PixPaymentPanel"
import { usePayment } from "../../../hooks/Payment/usePayment"
import { useSubscriptionPlans } from "../../../hooks/Company/useSubscriptionPlans"
import { useGetCompany } from "../../../hooks/Company/useGetCompany"
import { SubscriptionEntity } from "../../../Objects/Company/SubscriptionEntity"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import THEME from "../../../configuration/Theme"
import { useConfirmation } from "../../../context/useConfirmationContext"
import { useMessage } from "../../../context/useMessageContext"
import { PlanUsageStatus } from "../../../Objects/Company/PlanUsageStatus"
import { PlanUsageAlert } from "../../../components/PlanUsageAlert/PlanUsageAlert"
import { usePlanUsage } from "../../../hooks/Company/usePlanUsage"
import { useSubscriptionBillingStatus } from "../../../hooks/Company/useSubscriptionBillingStatus"
import { SubscriptionBillingStatus } from "../../../Objects/Company/SubscriptionBillingStatus"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faArrowUpRightFromSquare,
  faCheck,
  faCircleInfo,
  faClock,
  faClose,
  faCreditCard,
  faEnvelope,
  faTicket,
  faTriangleExclamation,
  faUserTie,
} from "@fortawesome/free-solid-svg-icons"
import { ECommercialOriginType } from "../../../Enum/ECommercialOriginType"
import { SubscriptionCommercialOrigin } from "../../../Objects/CommercialReferral/SubscriptionCommercialOrigin"
import { EPaymentProcessingSeverityType } from "../../../Enum/EPaymentProcessingSeverityType"
import { SubscriptionPlanPricing } from "../../../Objects/Company/SubscriptionPlanPricing"
import { SubscriptionPurchaseResult } from "../../../Objects/Company/SubscriptionPurchaseResult"
import { PlanCouponValidationResult } from "../../../Objects/Company/PlanCouponValidationResult"
import { ResellerCodeValidationResult } from "../../../Objects/Company/ResellerCodeValidationResult"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import { SIZE } from "../../../configuration/Sizes"
import { usePublicContact } from "../../../context/usePublicContactContext"
import GetSpace from "../../../utils/space"
import { INPUT_SIZE } from "../../../configuration/InputSize"

interface PlanProps {
  onClose: () => void
  planUsageStatus?: PlanUsageStatus | null
}

interface PlanContentProps extends PlanProps {
  showCloseButton?: boolean
  containerClassName?: string
  forceRefreshPlanUsage?: boolean
}

const getStatusBadge = (subscription: SubscriptionEntity, t: (key: string) => string) => {
  if (subscription.isCancelled()) return { label: t("my_company.status_cancelled"), color: "bg-gray-400" }

  if (subscription.isActive()) return { label: t("my_company.status_active"), color: "bg-green-500" }

  if (subscription.isDelinquent) return { label: t("my_company.status_delinquent"), color: "bg-red-500" }

  if (subscription.needsPayment()) return { label: t("my_company.status_pending_payment"), color: "bg-yellow-500" }

  return { label: t("my_company.status_processing"), color: "bg-orange-400" }
}

const SubscriptionSupportContact = ({ compact = false }: { compact?: boolean }) => {
  const { t } = useTranslation("frontend")

  const { contact } = usePublicContact()

  if (!contact.email && !contact.whatsAppPhone) {
    return null
  }

  return (
    <div className={`${compact ? "mt-2" : ""} flex flex-col gap-1 text-xs font-semibold text-slate-700`}>
      <span className="text-slate-500">{t("my_company.subscription_support_contact_title")}</span>

      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-1 sm:gap-3">
        {contact.emailUrl && (
          <a
            className="text-blue-700 hover:underline"
            href={contact.emailUrl}
            data-interaction-description-key="interactionDescriptions:pages.myRestaurant.myCompany.planPopup.link.subscriptionSupportEmail"
          >
            {t("my_company.subscription_support_email", { email: contact.email })}
          </a>
        )}

        {contact.whatsappUrl && (
          <a
            className="text-green-700 hover:underline"
            href={contact.whatsappUrl}
            target="_blank"
            rel="noreferrer"
            data-interaction-description-key="interactionDescriptions:pages.myRestaurant.myCompany.planPopup.link.subscriptionSupportWhatsapp"
          >
            {t("my_company.subscription_support_whatsapp", { phone: contact.whatsappLabel })}
          </a>
        )}
      </div>
    </div>
  )
}

export const PlanContent = ({
  onClose,
  planUsageStatus = null,
  showCloseButton = true,
  containerClassName = defaultStyle.alert2,
  forceRefreshPlanUsage = false,
}: PlanContentProps) => {
  const { t } = useTranslation("frontend")

  const [planType, setPlanType] = useState<EAccountPlansType>(companyMemory.subscriptionEntity.eAccountPlansType)

  const { handleGetCompany } = useGetCompany()

  const { setNewConfirmation } = useConfirmation()

  const { handleGetPlanUsage } = usePlanUsage()

  const { setMessage } = useMessage()

  const {
    purchaseSubscription,
    validatePlanCoupon,
    cancelSubscriptionPlan,
    requestSubscriptionSupport,
    validateResellerCode,
  } = usePayment()

  const { handleGetSubscriptionBillingStatus } = useSubscriptionBillingStatus()

  const { getSubscriptionPlans } = useSubscriptionPlans()

  const [subscription, setSubscription] = useState<SubscriptionEntity>(
    () => new SubscriptionEntity(companyMemory.subscriptionEntity),
  )

  const [currentPlanUsageStatus, setPlanUsageStatus] = useState<PlanUsageStatus | null>(planUsageStatus)

  const [billingStatus, setBillingStatus] = useState<SubscriptionBillingStatus | null>(null)

  const [planPricing, setPlanPricing] = useState<SubscriptionPlanPricing[]>([])

  const [commercialOriginType, setCommercialOriginType] = useState<number>(0)

  const [commercialReferralCode, setCommercialReferralCode] = useState("")

  const [resellerCodeValidation, setResellerCodeValidation] = useState<ResellerCodeValidationResult | null>(null)

  const [periodType, setPeriodType] = useState<EBillingPeriodType>(EBillingPeriodType.Monthly)

  const [chargeMethod, setChargeMethod] = useState<EChargeMethodType>(EChargeMethodType.Pix)

  const [payerEmail, setPayerEmail] = useState("")

  const [couponCode, setCouponCode] = useState("")

  const [couponResult, setCouponResult] = useState<PlanCouponValidationResult | null>(null)

  const [purchaseResult, setPurchaseResult] = useState<SubscriptionPurchaseResult | null>(null)

  const [isPurchasePopupOpen, setIsPurchasePopupOpen] = useState(false)

  const status = getStatusBadge(subscription, t)

  const shouldShowStatusSupportContact =
    !subscription.isFree() &&
    (!subscription.isActive() ||
      subscription.isDelinquent ||
      subscription.needsPayment() ||
      (billingStatus !== null && billingStatus.processingSeverity !== EPaymentProcessingSeverityType.None))

  const commissionOrigins = [ECommercialOriginType.Reseller]

  const shouldSelectReferralCode = commissionOrigins.includes(commercialOriginType)

  const isCommercialOriginLocked = subscription.isActive()

  const lockedCommercialOriginType = subscription.commercialOriginType

  const lockedCommercialReferralCode = subscription.commercialReferralCode ?? ""

  const mercadoPagoSubscriptionItems = t("my_company.mercado_pago_subscription_help_items", {
    returnObjects: true,
  }) as string[]

  const shouldShowCommercialOriginSelector = !isCommercialOriginLocked

  const selectedPlanPricing =
    planPricing.find((item) => item.planType === planType) ??
    new SubscriptionPlanPricing({
      planType,
      price: planType === subscription.eAccountPlansType ? subscription.currentChargeAmount : 0,
    })

  const availablePeriods = selectedPlanPricing.periods

  const pixEnabled = selectedPlanPricing.pixEnabled

  const selectedPeriodPricing =
    availablePeriods.find((item) => item.periodType === periodType) ?? availablePeriods[0] ?? null

  const purchaseGrossAmount = couponResult?.isValid ? couponResult.grossAmount : (selectedPeriodPricing?.price ?? 0)

  const purchaseDiscountAmount = couponResult?.isValid ? couponResult.discountAmount : 0

  const purchaseFinalAmount = couponResult?.isValid ? couponResult.finalAmount : (selectedPeriodPricing?.price ?? 0)

  const showPurchasePopup = isPurchasePopupOpen && subscription.status != "pending" && !billingStatus?.isActionBlocked

  const hasPlanSelectionChanged = planType !== EAccountPlansType.None && planType !== subscription.eAccountPlansType

  const handleOpenPurchasePopup = useCallback(() => {
    setPlanType(subscription.eAccountPlansType)
    setIsPurchasePopupOpen(true)
  }, [subscription.eAccountPlansType])

  const handleClosePurchasePopup = useCallback(() => {
    setIsPurchasePopupOpen(false)
    setPlanType(subscription.eAccountPlansType)
    setCouponCode("")
    setCouponResult(null)
    setPurchaseResult(null)
  }, [subscription.eAccountPlansType])

  const commercialOriginTypeRef = useRef<number>(commercialOriginType)

  const commercialReferralCodeRef = useRef<string>(commercialReferralCode)

  const resellerCodeValidationRef = useRef<ResellerCodeValidationResult | null>(resellerCodeValidation)

  const planTypeRef = useRef<EAccountPlansType>(planType)

  const getSubscriptionPlansRef = useRef(getSubscriptionPlans)

  const handleGetSubscriptionBillingStatusRef = useRef(handleGetSubscriptionBillingStatus)

  const hasForcedUsageRefreshRef = useRef(false)

  const syncSubscriptionFromMemory = useCallback(() => {
    const nextSubscription = new SubscriptionEntity(companyMemory.subscriptionEntity)

    setSubscription(nextSubscription)
    setPlanType(nextSubscription.eAccountPlansType)
  }, [])

  const refreshBillingStatus = useCallback(() => {
    handleGetSubscriptionBillingStatusRef.current((data) => {
      setBillingStatus(data)
    })
  }, [])

  useEffect(() => {
    commercialOriginTypeRef.current = commercialOriginType
  }, [commercialOriginType])

  useEffect(() => {
    commercialReferralCodeRef.current = commercialReferralCode
  }, [commercialReferralCode])

  useEffect(() => {
    resellerCodeValidationRef.current = resellerCodeValidation
  }, [resellerCodeValidation])

  useEffect(() => {
    planTypeRef.current = planType
  }, [planType])

  useEffect(() => {
    setCouponResult(null)
    setPurchaseResult(null)

    if (availablePeriods.length && !availablePeriods.some((item) => item.periodType === periodType)) {
      setPeriodType(availablePeriods[0].periodType)
    }
  }, [planType, availablePeriods.length])

  useEffect(() => {
    setCouponResult(null)
  }, [periodType, couponCode])

  useEffect(() => {
    setResellerCodeValidation(null)
  }, [commercialOriginType, commercialReferralCode])

  useEffect(() => {
    if (!pixEnabled && chargeMethod === EChargeMethodType.Pix) {
      setChargeMethod(EChargeMethodType.CheckoutRedirect)
    }
  }, [pixEnabled, chargeMethod])

  useEffect(() => {
    if (purchaseResult && purchaseResult.method === EChargeMethodType.CheckoutRedirect) {
      window.open(purchaseResult.initPointUrl, "_blank", "noreferrer")
    }
  }, [purchaseResult])

  useEffect(() => {
    getSubscriptionPlansRef.current = getSubscriptionPlans
  }, [getSubscriptionPlans])

  useEffect(() => {
    handleGetSubscriptionBillingStatusRef.current = handleGetSubscriptionBillingStatus
  }, [handleGetSubscriptionBillingStatus])

  useEffect(() => {
    const planType = companyMemory.subscriptionEntity.eAccountPlansType

    if (planType === EAccountPlansType.Scale) return

    if (forceRefreshPlanUsage) {
      if (hasForcedUsageRefreshRef.current) return

      hasForcedUsageRefreshRef.current = true
    } else if (currentPlanUsageStatus !== null) {
      return
    }

    handleGetPlanUsage((usage: PlanUsageStatus) => {
      setPlanUsageStatus({ ...usage })
    })
  }, [currentPlanUsageStatus, forceRefreshPlanUsage, handleGetPlanUsage, planUsageStatus])

  useEffect(() => {
    refreshBillingStatus()

    getSubscriptionPlansRef
      .current()
      .then((items) => {
        setPlanPricing(items)
      })
      .catch(() => {
        setPlanPricing([])
      })
  }, [refreshBillingStatus])

  return (
    <div className={containerClassName}>
      <div className="flex items-center justify-between px-6 py-4">
        <h2 className="text-xl font-black text-gray-800">{t("my_company.plan_upgrade_title")}</h2>

        <div className="flex items-center gap-1">
          <PopupHelpButton
            helpKey="pages.myrestaurant.mycompany.planpopup.1"
            variant="global"
            title={t("helpPopup:global.planUpgrade.title")}
            description={t("helpPopup:global.planUpgrade.description")}
            bullets={t("helpPopup:global.planUpgrade.bullets", { returnObjects: true }) as string[]}
          />

          {showCloseButton && (
            <ButtonIcon
              sizeNumber={SIZE.buttonCloseIconSize}
              bgColor={THEME.colors.buttonBGCloseColor}
              iconColor={THEME.colors.buttonFGCloseColor}
              icon={faClose}
              onClick={onClose}
              size="small"
              descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.planPopup.button.onClose"
            />
          )}
        </div>
      </div>

      {currentPlanUsageStatus && (
        <div className="m-2">
          <PlanUsageAlert planUsageStatus={currentPlanUsageStatus} />
        </div>
      )}

      {subscription.needsPayment() && (
        <Animate type={AnimationTypes.fadeInTraslate}>
          <div className="bg-yellow-50 mx-2 ring-2 ring-yellow-300 text-yellow-800 p-3 rounded-xl mt-4">
            <p>{t("my_company.pending_payment")}</p>
            <SubscriptionSupportContact compact />
          </div>
        </Animate>
      )}

      {subscription.isDelinquent && (
        <Animate type={AnimationTypes.fadeInTraslate}>
          <div className="bg-red-50 mx-2 ring-2 ring-red-300 text-red-700 p-3 rounded-xl mt-4">
            <p>{t("my_company.payment_failed")}</p>
            <SubscriptionSupportContact compact />
          </div>
        </Animate>
      )}

      {billingStatus && billingStatus.processingSeverity !== EPaymentProcessingSeverityType.None && (
        <Animate type={AnimationTypes.fadeInTraslate}>
          <div className="mx-2 mt-2 rounded-lg p-2 shadow-sm border-2 border-orange-200">
            <div className="flex items-start gap-3">
              <FontAwesomeIcon icon={faClock} className="text-orange-500 text-lg mt-1" />

              <div>
                <p className="font-bold text-slate-800">{t("my_company.processing_payment_title")}</p>

                <p className="text-sm text-slate-600 mt-1">{billingStatus.averageProcessingTimeText}</p>

                <SubscriptionSupportContact compact />
              </div>
            </div>

            {billingStatus.processingSeverity === EPaymentProcessingSeverityType.Delayed && (
              <div className="flex items-start gap-3 mt-3 p-3 rounded-lg bg-amber-50">
                <FontAwesomeIcon icon={faCircleInfo} className="text-orange-500 text-lg mt-1" />

                <p className="text-sm font-semibold text-amber-700">{t("my_company.processing_payment_delayed")}</p>
              </div>
            )}

            {billingStatus.processingSeverity === EPaymentProcessingSeverityType.Critical && (
              <div className="flex md:flex-row flex-col md:items-center md:justify-between gap-2 mt-3 p-2 rounded-lg ring-1 ring-red-200">
                <div className="flex items-start gap-3">
                  <FontAwesomeIcon icon={faTriangleExclamation} className="text-orange-500 text-lg mt-1" />

                  <p className="text-sm font-bold text-red-700">{t("my_company.processing_payment_critical")}</p>
                </div>

                {billingStatus.canRequestSupport && (
                  <div className="md:mt-0 mt-2">
                    <Button
                      type="primary"
                      typeButton="button"
                      size="medium"
                      label={t("my_company.request_support")}
                      bgColor={THEME.colors.buttonBGCloseColor}
                      animationTypes={AnimationTypes.fadeIn}
                      onClick={() => {
                        requestSubscriptionSupport(t("my_company.request_support_subject"), () => {
                          setMessage(t("my_company.support_called_title"), t("my_company.support_called_description"))
                        })
                      }}
                      descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.planPopup.button.requestSupport"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </Animate>
      )}

      {!subscription.isFree() && (
        <Animate type={AnimationTypes.fadeInTraslate}>
          <div className="bg-white mx-2 p-4 rounded-lg ring-2 ring-orange-300 mt-2">
            <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-3">
              <div>
                <p className="font-bold text-gray-700 mb-2">{t("my_company.subscription_status")}</p>

                <span className={`px-3 py-1 text-white text-xs rounded-full ${status.color}`}>{status.label}</span>
              </div>

              {shouldShowStatusSupportContact && <SubscriptionSupportContact />}

              {subscription.nextPaymentDate &&
                subscription.lastPaymentDate &&
                subscription.nextPaymentDate > subscription.lastPaymentDate && (
                  <div className="text-right">
                    <p className="text-sm text-gray-500">{t("my_company.next_billing")}</p>
                    <p className="font-bold">{subscription.nextPaymentDate.toString().toReadableDate()}</p>
                  </div>
                )}
            </div>
          </div>
        </Animate>
      )}

      {subscription.lastPaymentDate && (
        <Animate type={AnimationTypes.fadeInTraslate}>
          <div className="mt-2 mx-2 rounded-lg bg-white shadow-sm ring-2 ring-orange-300 p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-orange-500"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <rect x="2" y="5" width="20" height="14" rx="2" />
                  <line x1="2" y1="10" x2="22" y2="10" />
                </svg>

                <h4 className="text-base font-semibold text-gray-800">{t("my_company.last_payment")}</h4>
              </div>

              <span
                className={`flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full
            ${
              subscription.lastPaymentStatus === "approved"
                ? "bg-green-100 text-green-700"
                : subscription.lastPaymentStatus === "rejected"
                  ? "bg-red-100 text-red-700"
                  : "bg-yellow-100 text-yellow-700"
            } `}
              >
                {subscription.lastPaymentStatus === "approved" && (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}

                {subscription.lastPaymentStatus === "rejected" && (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                    <line x1="6" y1="6" x2="18" y2="18" />
                    <line x1="6" y1="18" x2="18" y2="6" />
                  </svg>
                )}

                {subscription.lastPaymentStatus !== "approved" && subscription.lastPaymentStatus !== "rejected" && (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                )}

                {subscription.lastPaymentStatus}
              </span>
            </div>

            <div className="space-y-3 text-sm text-gray-600">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-gray-500">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <rect x="3" y="4" width="18" height="18" rx="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                  <span>{t("my_company.date_label")}</span>
                </div>

                <span className="font-medium text-gray-800">
                  {subscription.lastPaymentDate.toString().toReadableDate()}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-gray-500">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <rect x="2" y="6" width="20" height="12" rx="2" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>

                  <span>{t("my_company.amount_paid_now")}</span>
                </div>

                <span className="font-semibold text-gray-900">
                  {(billingStatus?.lastPaymentAmount ?? subscription.currentChargeAmount).toMoney()}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-gray-500">{t("my_company.next_billing")}</span>

                <span className="font-semibold text-gray-900">
                  {(billingStatus?.currentChargeAmount ?? subscription.currentChargeAmount).toMoney()}
                </span>
              </div>

              {billingStatus?.nextPaymentDate && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">{t("my_company.next_cycle")}</span>

                  <span className="font-semibold text-gray-900">
                    {billingStatus.nextPaymentDate.toString().toReadableDate()}
                  </span>
                </div>
              )}
            </div>
          </div>
        </Animate>
      )}

      <Animate type={AnimationTypes.fadeInTraslate}>
        <div className="mt-2 mx-2 flex flex-col md:flex-row justify-between items-center gap-6 p-5 rounded-lg bg-white ring-2 ring-orange-300 shadow-sm">
          <div className="flex flex-col items-center md:items-start text-center md:text-left">
            <h3 className="font-extrabold text-orange-600 text-lg mb-3">{t("my_company.current_plan")}</h3>

            <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-6 text-sm text-gray-700">
              <p>
                {t("my_company.plan_label")}:{" "}
                <strong className="text-gray-900">
                  {subscription.eAccountPlansType.toEAccountPlansTypeToString()}
                </strong>
              </p>

              <p className="font-semibold text-gray-900">
                {subscription.currentChargeAmount.toMoney()}
                {subscription.currentPeriodType === EBillingPeriodType.Monthly ? ` ${t("my_company.per_month")}` : ""}
              </p>
            </div>
          </div>

          {subscription.status != "pending" && !billingStatus?.isActionBlocked && (
            <div className="flex flex-col md:flex-row items-center gap-3 w-full md:w-auto">
              <Button
                type="primary"
                typeButton="button"
                size="medium"
                label={t("my_company.change_plan_button")}
                bgColor={THEME.colors.buttonBGSaveColor}
                animationTypes={AnimationTypes.fadeIn}
                onClick={handleOpenPurchasePopup}
                descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.planPopup.button.openPurchasePopup"
              />

              {subscription.isActive() && subscription.eAccountPlansType !== EAccountPlansType.None && (
                <Button
                  type="primary"
                  typeButton="button"
                  size="medium"
                  label={t("my_company.cancel_subscription")}
                  animationTypes={AnimationTypes.fadeInTraslate}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  onClick={() => {
                    setNewConfirmation(t("my_company.cancel_plan_title"), t("my_company.cancel_plan_confirm"), () => {
                      cancelSubscriptionPlan(() => {
                        handleGetCompany(() => {
                          syncSubscriptionFromMemory()
                          refreshBillingStatus()

                          setMessage(
                            t("my_company.cancel_subscription_success_title"),
                            t("my_company.cancel_subscription_success_description"),
                          )

                          onClose()
                        })
                      })
                    })
                  }}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.planPopup.button.cancelSubscription"
                />
              )}
            </div>
          )}
        </div>
      </Animate>

      {showPurchasePopup && (
        <ModalPortal>
          <div className={defaultStyle.overlay}>
            <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
              <div className={defaultStyle.alert1}>
                <TitlePageResponsiveIconSmall
                  title={
                    hasPlanSelectionChanged
                      ? t("my_company.purchase_popup_title", {
                          plan: planType.toEAccountPlansTypeToString(),
                        })
                      : t("my_company.change_plan_button")
                  }
                  icon={faCreditCard}
                  iconColor={THEME.colors.iconBGColor}
                  children={
                    <ButtonIcon
                      sizeNumber={SIZE.buttonCloseIconSize}
                      bgColor={THEME.colors.buttonBGCloseColor}
                      iconColor={THEME.colors.buttonFGCloseColor}
                      icon={faClose}
                      onClick={handleClosePurchasePopup}
                      size="small"
                      descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.planPopup.button.onClosePurchasePopup"
                    />
                  }
                />

                <div className="px-2 pb-4 mt-2">
                  {!purchaseResult && (
                    <>
                      {!hasPlanSelectionChanged && (
                        <div className="mt-2 rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-700">
                          {t("my_company.purchase_popup_select_plan_hint")}
                        </div>
                      )}

                      <GenericDropDown
                        data={Object.mapEnumToStrings(EAccountPlansType)}
                        name="plan"
                        animationDuration="2s"
                        initialState={planType}
                        handlerFuncion={(e) => {
                          setPlanType(e.target.selectedIndex)
                        }}
                        descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.planPopup.select.plan"
                      />
                    </>
                  )}

                  <GetSpace top={5} />

                  {hasPlanSelectionChanged && (
                    <>
                      {shouldShowCommercialOriginSelector && (
                        <GenericDropDown
                          data={Object.mapEnumToStrings(ECommercialOriginType)}
                          name="origem"
                          animationDuration="2s"
                          initialState={commercialOriginType}
                          handlerFuncion={(e) => {
                            setCommercialOriginType(e.target.selectedIndex)
                           
                            setCommercialReferralCode("")
                          }}
                          descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.planPopup.select.originType"
                        />
                      )}
                    </>
                  )}

                  {hasPlanSelectionChanged &&
                    (purchaseResult ? (
                      <div className="mt-2">
                        {purchaseResult.method === EChargeMethodType.Pix ? (
                          <PixPaymentPanel
                            purchase={purchaseResult}
                            onConfirmed={() => {
                              handleGetCompany(() => {
                                syncSubscriptionFromMemory()

                                refreshBillingStatus()

                                handleClosePurchasePopup()
                                onClose()
                              })
                            }}
                          />
                        ) : (
                          <div className="rounded-xl ring-2 ring-orange-300 bg-white p-6 text-center">
                            <h4 className="font-black text-gray-800 text-lg">{t("my_company.redirect_panel_title")}</h4>

                            <p className="text-sm text-gray-500 mt-1">{t("my_company.redirect_panel_description")}</p>

                            <p className="text-xs text-gray-400 mt-1">{t("my_company.redirect_panel_fallback_hint")}</p>

                            <div className="mt-4">
                              <Button
                                type="primary"
                                typeButton="button"
                                size="medium"
                                label={t("my_company.redirect_button")}
                                bgColor={THEME.colors.buttonBGSaveColor}
                                animationTypes={AnimationTypes.fadeIn}
                                onClick={() => {
                                  window.open(purchaseResult.initPointUrl, "_blank", "noreferrer")
                                }}
                                descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.planPopup.button.redirectCheckout"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <>
                        <GetSpace top={5} />

                        {availablePeriods.length > 0 && (
                          <>
                            <GenericDropDown
                              data={availablePeriods.map((item) => item.periodType.toEBillingPeriodTypeToString())}
                              name="periodo"
                              animationDuration="2s"
                              initialState={availablePeriods.findIndex((item) => item.periodType === periodType)}
                              handlerFuncion={(e) => {
                                setPeriodType(
                                  availablePeriods[e.target.selectedIndex]?.periodType ?? EBillingPeriodType.Monthly,
                                )
                              }}
                              descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.planPopup.select.periodType"
                            />

                            {pixEnabled && (
                              <>
                                <GetSpace top={5} />

                                <GenericDropDown
                                  data={[t("my_company.purchase_method_pix"), t("my_company.purchase_method_redirect")]}
                                  name="metodo"
                                  animationDuration="2s"
                                  initialState={chargeMethod}
                                  handlerFuncion={(e) => {
                                    setChargeMethod(e.target.selectedIndex as EChargeMethodType)
                                  }}
                                  descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.planPopup.select.chargeMethod"
                                />
                              </>
                            )}

                            <GetSpace top={10} />

                            <InputField
                              type="email"
                              value={payerEmail}
                              placeholder={t("my_company.email_placeholder")}
                              icon={faEnvelope}
                              required
                              onChange={(e) => setPayerEmail(e.target.value)}
                              descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.planPopup.input.payerEmail"
                            />

                            {shouldShowCommercialOriginSelector && shouldSelectReferralCode && (
                              <div className="mt-3">
                                <div className="flex">
                                  <InputField
                                    type="text"
                                    value={commercialReferralCode}
                                    placeholder={t("my_company.available_seller_code")}
                                    icon={faUserTie}
                                    maxLength={20}
                                    onChange={(e) => setCommercialReferralCode(e.target.value.toUpperCase())}
                                    descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.planPopup.input.resellerCode"
                                  />

                                  <GetSpace left={5} />

                                  <ButtonIcon
                                    icon={faCheck}
                                    sizeNumber={SIZE.buttonCloseIconSize}
                                    bgColor={THEME.colors.buttonBGPlusColor}
                                    animationTypes={AnimationTypes.fadeIn}
                                    onClick={() => {
                                      if (!commercialReferralCode.trim()) return

                                      validateResellerCode(
                                        commercialReferralCode.trim(),
                                        (result: ResellerCodeValidationResult) => {
                                          setResellerCodeValidation(result)
                                        },
                                      )
                                    }}
                                    descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.planPopup.button.applyResellerCode"
                                  />
                                </div>

                                {resellerCodeValidation && resellerCodeValidation.isValid && (
                                  <p className="mt-1 text-xs font-semibold text-green-700">
                                    {t("my_company.reseller_code_valid", { name: resellerCodeValidation.resellerName })}
                                  </p>
                                )}

                                {resellerCodeValidation && !resellerCodeValidation.isValid && (
                                  <p className="mt-1 text-xs font-semibold text-red-600">
                                    {resellerCodeValidation.errorMessage ?? t("my_company.reseller_code_invalid")}
                                  </p>
                                )}
                              </div>
                            )}

                            <GetSpace top={10} />

                            <div className="flex">
                              <InputField
                                type="text"
                                value={couponCode}
                                placeholder={t("my_company.coupon_placeholder")}
                                icon={faTicket}
                                maxLength={40}
                                onChange={(e) => setCouponCode(e.target.value)}
                                descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.planPopup.input.couponCode"
                              />

                              <GetSpace left={5} />

                              <ButtonIcon
                                icon={faCheck}
                                sizeNumber={SIZE.buttonCloseIconSize}
                                bgColor={THEME.colors.buttonBGPlusColor}
                                animationTypes={AnimationTypes.fadeIn}
                                onClick={() => {
                                  if (!couponCode.trim()) return

                                  validatePlanCoupon(
                                    couponCode.trim(),
                                    planType,
                                    periodType,
                                    (result: PlanCouponValidationResult) => {
                                      setCouponResult(result)

                                      if (result.isValid) {
                                        setMessage(
                                          t("my_company.coupon_label"),
                                          t("my_company.coupon_applied_success", {
                                            amount: result.discountAmount.toMoney(),
                                          }),
                                        )
                                      }
                                    },
                                  )
                                }}
                                descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.planPopup.button.applyCoupon"
                              />
                            </div>

                            {couponResult && couponResult.isValid && couponResult.resellerName && (
                              <p className="mt-1 text-xs font-semibold text-green-700">
                                {t("my_company.reseller_code_valid", { name: couponResult.resellerName })}
                              </p>
                            )}

                            {couponResult && !couponResult.isValid && (
                              <p className="mt-1 text-xs font-semibold text-red-600">
                                {couponResult.errorMessage ?? t("my_company.coupon_invalid")}
                              </p>
                            )}
                          </>
                        )}

                        <GetSpace top={5} />

                        <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                          <div className="mt-4 rounded-lg bg-slate-50 p-3 text-sm text-slate-700 space-y-1">
                            <div className="flex justify-between">
                              <span>{t("my_company.purchase_summary_gross")}</span>

                              <span className="font-semibold">{purchaseGrossAmount.toMoney()}</span>
                            </div>

                            {!shouldShowCommercialOriginSelector && lockedCommercialReferralCode && (
                              <div className="mt-3 rounded-md bg-slate-50 border border-slate-200 p-3 text-sm text-slate-700">
                                {t("my_company.subscription_commercial_origin_locked_code", {
                                  code: lockedCommercialReferralCode,
                                })}
                              </div>
                            )}

                            {purchaseDiscountAmount > 0 && (
                              <div className="flex justify-between text-green-700">
                                <span>{t("my_company.purchase_summary_discount")}</span>

                                <span className="font-semibold">-{purchaseDiscountAmount.toMoney()}</span>
                              </div>
                            )}

                            <div className="flex justify-between text-base font-black text-slate-900">
                              <span>{t("my_company.purchase_summary_final")}</span>

                              <span>{purchaseFinalAmount.toMoney()}</span>
                            </div>
                          </div>
                        </Animate>

                        <div className="mt-4">
                          <Button
                            type="primary"
                            typeButton="button"
                            size="medium"
                            label={t("my_company.purchase_button")}
                            bgColor={THEME.colors.buttonBGSaveColor}
                            animationTypes={AnimationTypes.fadeIn}
                            disabled={!payerEmail.trim() || !selectedPeriodPricing}
                            onClick={() => {
                              purchaseSubscription(
                                planTypeRef.current,
                                periodType,
                                payerEmail.trim(),
                                chargeMethod,
                                couponResult?.isValid ? couponCode.trim() : null,
                                new SubscriptionCommercialOrigin({
                                  originType: shouldShowCommercialOriginSelector
                                    ? commercialOriginTypeRef.current
                                    : lockedCommercialOriginType,
                                  commercialReferralCode: shouldShowCommercialOriginSelector
                                    ? resellerCodeValidationRef.current?.isValid
                                      ? commercialReferralCodeRef.current
                                      : ""
                                    : lockedCommercialReferralCode,
                                }),
                                (result: SubscriptionPurchaseResult) => {
                                  setPurchaseResult(result)
                                },
                              )
                            }}
                            descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.planPopup.button.purchaseSubscription"
                          />
                        </div>
                      </>
                    ))}
                </div>
              </div>
            </Animate>
          </div>
        </ModalPortal>
      )}

      {billingStatus?.isActionBlocked && (
        <div className="mx-2 mt-2 rounded-lg bg-amber-50 ring-2 ring-amber-200 p-4 text-amber-800">
          {t("my_company.action_blocked_by_processing")}
        </div>
      )}

      <div className="mt-10 p-6">
        <Pricing showButton={false} />
      </div>
    </div>
  )
}

export const PlanPopup = ({ onClose, planUsageStatus = null }: PlanProps) => (
  <ModalPortal>
    <div className={defaultStyle.overlay}>
      <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
        <PlanContent onClose={onClose} planUsageStatus={planUsageStatus} />
      </Animate>
    </div>
  </ModalPortal>
)
